import React from 'react';
import { Box, Card, CardContent, Typography } from '@mui/material';
import { Moment } from 'moment-timezone';
import { FridgeTemp, ParsedSensorInfo } from '../atoms-types/eSensors';
import HvacSparkline from './HvacSparkline';
import SensorIdentityTooltip from './SensorIdentityTooltip';
import {
  FRIDGE_WARNING_F,
  getDisplayClass,
  getDisplayLocation,
  getHvacStatus,
  getWarningThreshold,
  HVAC_STATUS_COLORS,
  HVAC_STATUS_LABELS,
} from './hvacStatus';
import { useFridgeTempHistory } from './useFridgeTempHistory';
import { formatLastSeen, SENSOR_OFFLINE_BORDER_COLOR } from '../atoms-types/sensorFreshness';

interface HvacUnitCardProps {
  sensor: ParsedSensorInfo;
  selected: boolean;
  startDateTime: Moment;
  endDateTime: Moment;
  onSelect: () => void;
}

const HvacUnitCard: React.FC<HvacUnitCardProps> = ({
  sensor,
  selected,
  startDateTime,
  endDateTime,
  onSelect,
}) => {
  const tempF = (sensor.jInfo as FridgeTemp | undefined)?.TempF;
  const thresholds = { warningF: sensor.warningF, alarmF: sensor.alarmF };
  const status = getHvacStatus(typeof tempF === 'number' ? tempF : null, sensor.LastRx, thresholds);
  const statusColor = HVAC_STATUS_COLORS[status];
  const offline = status === 'offline';
  // An offline sensor's last reading is stale; show 0 instead of the stale temp.
  const displayTempF = offline ? 0 : tempF;
  const lastSeen = formatLastSeen(sensor.LastRx);
  const name = getDisplayLocation(sensor) || sensor.deviceEUI.slice(-6);
  const classLabel = getDisplayClass(sensor);
  const borderColor = offline
    ? SENSOR_OFFLINE_BORDER_COLOR
    : selected
      ? statusColor
      : 'divider';

  const { points } = useFridgeTempHistory(
    sensor.deviceEUI,
    startDateTime,
    endDateTime,
    getWarningThreshold(thresholds) || FRIDGE_WARNING_F,
    offline ? null : (typeof tempF === 'number' ? tempF : null),
  );

  return (
    <Card
      onClick={onSelect}
      elevation={selected ? 4 : 1}
      sx={{
        minHeight: 148,
        height: '100%',
        cursor: 'pointer',
        overflow: 'visible',
        border: '2px',
        borderStyle: offline ? 'dashed' : 'solid',
        borderColor,
        bgcolor: selected ? `${statusColor}14` : 'background.paper',
        transition: 'border-color 120ms ease, box-shadow 120ms ease, background-color 120ms ease',
        '&:hover': {
          borderColor: offline ? SENSOR_OFFLINE_BORDER_COLOR : statusColor,
          boxShadow: 3,
        },
      }}
    >
      <CardContent
        sx={{
          p: 1.25,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.75,
          '&:last-child': { pb: 1.25 },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: statusColor,
                flexShrink: 0,
              }}
            />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                lineHeight: 1.1,
                color:
                  status === 'alarm'
                    ? statusColor
                    : offline
                      ? statusColor
                      : 'text.primary',
              }}
            >
              {typeof displayTempF === 'number' ? `${displayTempF.toFixed(1)}°F` : '—'}
            </Typography>
          </Box>
          <Typography
            variant="caption"
            sx={{
              color: statusColor,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: 0.4,
              flexShrink: 0,
            }}
          >
            {HVAC_STATUS_LABELS[status]}
          </Typography>
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <SensorIdentityTooltip sensor={sensor}>
            <Typography
              variant="body2"
              noWrap
              sx={{
                fontWeight: 600,
                color: offline ? statusColor : 'text.primary',
                lineHeight: 1.25,
              }}
            >
              {name}
            </Typography>
          </SensorIdentityTooltip>
          <Typography
            variant="caption"
            noWrap
            title={classLabel || undefined}
            sx={{
              color: status === 'alarm' ? statusColor : offline ? statusColor : 'text.secondary',
              fontWeight: status === 'alarm' ? 600 : 400,
              lineHeight: 1.25,
              display: 'block',
            }}
          >
            {classLabel || '—'}
            {status === 'alarm' ? ' · Above threshold' : ''}
          </Typography>
        </Box>

        <Box
          sx={{
            width: '100%',
            height: 24,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <HvacSparkline points={points} color={statusColor} width={120} height={24} />
        </Box>

        <Typography
          variant="caption"
          noWrap
          title={`Last seen ${lastSeen}`}
          sx={{
            color: offline ? statusColor : 'text.secondary',
            fontWeight: offline ? 600 : 400,
            display: 'block',
            lineHeight: 1.35,
          }}
        >
          Last seen {lastSeen}
        </Typography>
      </CardContent>
    </Card>
  );
};

export default HvacUnitCard;
